import { useEffect } from 'react'
import './dme.scss'
import {
  IndexRoute,
  Route,
  RouteComponentProps,
  Router,
  withRouter,
} from 'react-router'
import { getHistory } from 'lib/history'
import {
  track,
  useTracking,
  withContextualizedTracking,
} from 'lib/babylistHealthAnalytics'
import HomeView, { HomeViewProps } from './views/HomeView'
import PLPView from './views/PLPView'
import CustomerInformationView from './views/CustomerInformationView'
import { LayoutContainer, LayoutPresenter } from './widgets/Layout'
import {
  CUSTOMER_INFORMATION_PATH,
  GUIDES_PATH,
  HOME_PATH,
  INSURANCE_PARTNER_PATH,
  ORDER_PATH,
  PHYSICIAN_RESULT_PATH,
  SEARCH_PHYSICIAN_PATH,
  STORE_NAME,
  STORE_PATH,
  PDP_PATH,
  SUBSCRIPTIONS_PATH,
} from './constants'
import GuidesView, { GuidesViewProps } from './views/GuidesView'
import PhysicianSearchView from './views/PhysicianSearchView'
import PDP from './views/PDPView'
import NotificationWidget from './widgets/NotificationWidget'
import { withDebugHelpers } from './utils'
import PhysicianResultView from './views/PhysicianResultView'
import InsurancePartnerView from './views/InsurancePartnerView'
import { UISettings } from './dme.types'
import OrderView from './views/OrderView'
import ReplacementPartsView, {
  CustomerInsuranceProps,
} from './views/ReplacementPartsView'

export interface DMEAppProps {
  homeView: HomeViewProps
  guidesView: GuidesViewProps
  settings: UISettings
  pdpProps: APIResponse.GenericProduct[]
  orderProps?: APIResponse.DME.Order
  catalogCategoryProps?: APIResponse.CatalogCategory
  customerInsuranceProps?: CustomerInsuranceProps
  subscriptionProps?: APIResponse.DME.Subscription
  invalidPartsOrderProps?: APIResponse.DME.SubscriptionError
}

interface PDPRouteParams {
  urlSlug: string
  id: string
  productId: string
}

interface OrderRouteParams {
  id: string
}

type RouteComponentWithoutParams = RouteComponentProps<
  Record<string, never>,
  Record<string, never>
>

const DMEApp = ({
  guidesView,
  homeView,
  settings,
  pdpProps,
  orderProps,
  catalogCategoryProps,
  customerInsuranceProps,
  subscriptionProps,
  invalidPartsOrderProps,
}: DMEAppProps) => {
  const tracker = useTracking()
  const history = getHistory({
    path: window.location.pathname,
    useRouter: true,
  })
  const storeName = STORE_NAME

  const fireHomeViewedEvent = () => {
    tracker.trackEvent({ event: track.homeViewed, storeName })
  }

  const fireProductListViewedEvent = () => {
    tracker.trackEvent({ event: track.productListViewed, storeName })
  }

  const firePhysicianSearchViewedEvent = () => {
    tracker.trackEvent({
      event: track.physicianSearchViewed,
      storeName,
    })
  }

  const fireCustomerInformationFormViewedEvent = () => {
    tracker.trackEvent({
      event: track.customerInformationFormViewed,
      storeName,
    })
  }

  const firePDPViewedEvent = (price: number) => {
    tracker.trackEvent({
      storeName,
      event: track.pdpViewed,
      productPrice: price,
      productType: 'Product',
    })
  }

  useEffect(() => {
    history.listen(() => {
      window.scrollTo(0, 0)
    })
  }, [])

  return (
    <LayoutContainer settings={settings}>
      <Router history={history}>
        <Route component={LayoutPresenter}>
          <Route
            component={(props: RouteComponentWithoutParams) => (
              <HomeView
                guides={homeView.guides || []}
                onComponentDidMount={fireHomeViewedEvent}
                {...props}
              />
            )}
            path={HOME_PATH}
          />
          <Route
            component={(props: RouteComponentWithoutParams) => (
              <PLPView
                onComponentDidMount={fireProductListViewedEvent}
                {...props}
              />
            )}
            path={STORE_PATH}
          />
          <Route path={CUSTOMER_INFORMATION_PATH}>
            <IndexRoute
              component={(props: RouteComponentWithoutParams) => (
                <CustomerInformationView
                  {...props}
                  onComponentDidMount={fireCustomerInformationFormViewedEvent}
                />
              )}
            />
            <Route
              component={(props: RouteComponentWithoutParams) => (
                <PhysicianSearchView
                  {...props}
                  onComponentDidMount={firePhysicianSearchViewedEvent}
                />
              )}
              path={SEARCH_PHYSICIAN_PATH}
            />
          </Route>
          <Route component={PhysicianResultView} path={PHYSICIAN_RESULT_PATH} />
          <Route
            component={(props) => <GuidesView {...props} {...guidesView} />}
            path={GUIDES_PATH}
          />
          <Route
            component={(
              props: RouteComponentProps<PDPRouteParams, PDPRouteParams>
            ) => (
              <PDP
                {...props}
                pdpProps={pdpProps}
                onComponentDidMount={firePDPViewedEvent}
              />
            )}
            path={PDP_PATH}
          />
          <Route
            component={InsurancePartnerView}
            path={INSURANCE_PARTNER_PATH}
          />
          <Route
            component={(
              routeProps: RouteComponentProps<
                OrderRouteParams,
                OrderRouteParams
              >
            ) =>
              subscriptionProps &&
              catalogCategoryProps &&
              customerInsuranceProps ? (
                <ReplacementPartsView
                  {...routeProps}
                  heroProducts={catalogCategoryProps.data.heroProducts}
                  insurance={customerInsuranceProps}
                  subscription={subscriptionProps}
                  subscriptionError={invalidPartsOrderProps}
                  onComponentDidMount={() => undefined}
                />
              ) : null
            }
            path={SUBSCRIPTIONS_PATH}
          />
        </Route>
        <Route
          component={(
            routeProps: RouteComponentProps<OrderRouteParams, OrderRouteParams>
          ) =>
            orderProps ? (
              <OrderView
                {...routeProps}
                order={orderProps}
                onComponentDidMount={() => undefined}
              />
            ) : null
          }
          path={ORDER_PATH}
        />
      </Router>
      <NotificationWidget />
    </LayoutContainer>
  )
}

const DMEAppWithDebugging = withDebugHelpers(withRouter(DMEApp))
export default withContextualizedTracking()(DMEAppWithDebugging)
